import { useState, useEffect, useMemo } from 'react'
import { Calendar, Clock, MapPin, Users, Filter, Search, Bell, BellOff } from 'lucide-react'
import { Event } from '../../types'
import { apiService } from '../../services/api'
import { useAuth } from '../../hooks/useAuth'
import SafeHtmlContent from '../common/SafeHtmlContent'

interface EventListProps {
  className?: string
}

interface EventFilters {
  eventType: Event['eventType'] | 'all'
  timeFilter: 'all' | 'upcoming' | 'current' | 'today'
  signupOnly: boolean
  searchQuery: string
}

const EVENT_TYPE_LABELS: Record<Event['eventType'], string> = {
  talk: 'Talk',
  session: 'Session',
  bus: 'Bus',
  competition: 'Competition',
  other: 'Other'
}

const EVENT_TYPE_COLORS: Record<Event['eventType'], string> = {
  talk: 'bg-blue-100 text-blue-800',
  session: 'bg-green-100 text-green-800',
  bus: 'bg-yellow-100 text-yellow-800',
  competition: 'bg-red-100 text-red-800',
  other: 'bg-gray-100 text-gray-800'
}

export default function EventList({ className = '' }: EventListProps) {
  const { user } = useAuth()
  const [events, setEvents] = useState<Event[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [filters, setFilters] = useState<EventFilters>({
    eventType: 'all',
    timeFilter: 'upcoming',
    signupOnly: false,
    searchQuery: ''
  })
  const [showFilters, setShowFilters] = useState(false)
  const [signupLoading, setSignupLoading] = useState<string | null>(null)
  const [notificationsEnabled, setNotificationsEnabled] = useState(false)

  // Load events
  const loadEvents = async () => {
    try {
      setLoading(true)
      setError(null)

      console.log('[EventList] Loading events...')

      const queryParams = new URLSearchParams()
      
      if (filters.eventType !== 'all') {
        queryParams.append('eventType', filters.eventType)
      }
      
      if (filters.timeFilter === 'upcoming') {
        queryParams.append('upcoming', 'true')
      } else if (filters.timeFilter === 'current') {
        queryParams.append('current', 'true')
      } else if (filters.timeFilter === 'today') {
        const today = new Date().toISOString().split('T')[0]
        queryParams.append('startDate', today)
        queryParams.append('endDate', today + 'T23:59:59.999Z')
      }
      
      if (filters.signupOnly) {
        queryParams.append('signupOnly', 'true')
      }

      const endpoint = `/events${queryParams.toString() ? '?' + queryParams.toString() : ''}`
      console.log('[EventList] Making request to:', endpoint)
      
      const response = await apiService.request<{ events: Event[]; count: number }>(endpoint)

      if (response.error) {
        console.error('[EventList] API error:', response.error)
        
        // Handle authentication errors specifically
        if (response.error.code === 'AUTH_ERROR') {
          setError('Authentication failed. Please log in again.')
        } else {
          setError(response.error.message)
        }
        return
      }

      console.log('[EventList] Events loaded successfully:', response.data)
      
      // Handle both wrapped and unwrapped response formats
      const eventsData = (response.data as any)?.data || response.data
      const events = eventsData?.events || eventsData || []
      
      setEvents(events)
    } catch (err) {
      console.error('[EventList] Load events error:', err)
      setError(err instanceof Error ? err.message : 'Failed to load events')
    } finally {
      setLoading(false)
    }
  }

  // Filter events based on search query
  const filteredEvents = useMemo(() => {
    if (!filters.searchQuery.trim()) {
      return events
    }

    const query = filters.searchQuery.toLowerCase()
    return events.filter(event => 
      event.title.toLowerCase().includes(query) ||
      event.description.toLowerCase().includes(query) ||
      (event.location && event.location.toLowerCase().includes(query))
    )
  }, [events, filters.searchQuery])

  // Check notification permission status
  useEffect(() => {
    if ('Notification' in window) {
      setNotificationsEnabled(Notification.permission === 'granted')
    }
  }, [])

  // Load events when filters change
  useEffect(() => {
    loadEvents()
  }, [filters.eventType, filters.timeFilter, filters.signupOnly])

  // Listen for session expired events
  useEffect(() => {
    const handleSessionExpired = () => {
      console.log('[EventList] Session expired, redirecting to login')
      setError('Your session has expired. Please log in again.')
      // The AuthProvider will handle the redirect
    }

    window.addEventListener('auth:session-expired', handleSessionExpired)
    
    return () => {
      window.removeEventListener('auth:session-expired', handleSessionExpired)
    }
  }, [])

  // Handle event signup
  const handleSignup = async (eventId: string) => {
    if (!user) return

    try {
      setSignupLoading(eventId)
      const response = await apiService.signupForEvent(eventId)

      if (response.error) {
        throw new Error(response.error.message)
      }

      // Update local state
      setEvents(prevEvents => 
        prevEvents.map(event => 
          event.eventId === eventId 
            ? { ...event, participants: [...event.participants, user.userId] }
            : event
        )
      )
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to sign up for event')
    } finally {
      setSignupLoading(null)
    }
  }

  // Handle event withdrawal
  const handleWithdraw = async (eventId: string) => {
    if (!user) return

    try {
      setSignupLoading(eventId)
      const response = await apiService.withdrawFromEvent(eventId)

      if (response.error) {
        throw new Error(response.error.message)
      }

      // Update local state
      setEvents(prevEvents => 
        prevEvents.map(event => 
          event.eventId === eventId 
            ? { ...event, participants: event.participants.filter(id => id !== user.userId) }
            : event
        )
      )
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to withdraw from event')
    } finally {
      setSignupLoading(null)
    }
  }

  // Request notification permission
  const requestNotificationPermission = async () => {
    if ('Notification' in window) {
      const permission = await Notification.requestPermission()
      setNotificationsEnabled(permission === 'granted')
      
      if (permission === 'granted') {
        // Register for push notifications
        // This would typically involve registering a service worker
        // and subscribing to push notifications
        console.log('Notifications enabled')
      }
    }
  }

  // Format date and time
  const formatDateTime = (dateTime: string) => {
    const date = new Date(dateTime)
    const today = new Date()
    const tomorrow = new Date(today)
    tomorrow.setDate(tomorrow.getDate() + 1)

    const isToday = date.toDateString() === today.toDateString()
    const isTomorrow = date.toDateString() === tomorrow.toDateString()

    let dateStr = ''
    if (isToday) {
      dateStr = 'Today'
    } else if (isTomorrow) {
      dateStr = 'Tomorrow'
    } else {
      dateStr = date.toLocaleDateString('en-GB', { 
        weekday: 'short', 
        month: 'short', 
        day: 'numeric' 
      })
    }

    const timeStr = date.toLocaleTimeString('en-GB', { 
      hour: '2-digit', 
      minute: '2-digit' 
    })

    return { dateStr, timeStr }
  }

  // Check if user is signed up for event
  const isSignedUp = (event: Event) => {
    return user && event.participants.includes(user.userId)
  }

  // Check if event is full
  const isEventFull = (event: Event) => {
    return event.maxParticipants && event.participants.length >= event.maxParticipants
  }

  // Check if event is in the past
  const isEventPast = (event: Event) => {
    return new Date(event.endTime) < new Date()
  }

  if (loading) {
    return (
      <div className={`space-y-4 ${className}`}>
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded mb-4"></div>
          {[...Array(3)].map((_, i) => (
            <div key={i} className="bg-white rounded-lg p-4 shadow-sm border">
              <div className="h-6 bg-gray-200 rounded mb-2"></div>
              <div className="h-4 bg-gray-200 rounded mb-2 w-3/4"></div>
              <div className="h-4 bg-gray-200 rounded w-1/2"></div>
            </div>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Header with notifications toggle */}
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">Events</h2>
        
        <button
          onClick={requestNotificationPermission}
          className={`flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
            notificationsEnabled
              ? 'bg-green-100 text-green-800 hover:bg-green-200'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          {notificationsEnabled ? (
            <>
              <Bell className="h-4 w-4" />
              <span>Notifications On</span>
            </>
          ) : (
            <>
              <BellOff className="h-4 w-4" />
              <span>Enable Notifications</span>
            </>
          )}
        </button>
      </div>

      {/* Search and Filter Controls */}
      <div className="space-y-4">
        {/* Search Bar */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search events..."
            value={filters.searchQuery}
            onChange={(e) => setFilters(prev => ({ ...prev, searchQuery: e.target.value }))}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-fwo-blue focus:border-transparent"
          />
        </div>

        {/* Filter Toggle */}
        <div className="flex items-center justify-between">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors"
          >
            <Filter className="h-4 w-4" />
            <span>Filters</span>
          </button>
          
          <div className="text-sm text-gray-500">
            {filteredEvents.length} event{filteredEvents.length !== 1 ? 's' : ''}
          </div>
        </div>

        {/* Filter Controls */}
        {showFilters && (
          <div className="bg-gray-50 rounded-lg p-4 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Event Type Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Event Type
                </label>
                <select
                  value={filters.eventType}
                  onChange={(e) => setFilters(prev => ({ 
                    ...prev, 
                    eventType: e.target.value as EventFilters['eventType'] 
                  }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-fwo-blue focus:border-transparent"
                >
                  <option value="all">All Types</option>
                  {Object.entries(EVENT_TYPE_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              </div>

              {/* Time Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Time
                </label>
                <select
                  value={filters.timeFilter}
                  onChange={(e) => setFilters(prev => ({ 
                    ...prev, 
                    timeFilter: e.target.value as EventFilters['timeFilter'] 
                  }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-fwo-blue focus:border-transparent"
                >
                  <option value="all">All Events</option>
                  <option value="upcoming">Upcoming</option>
                  <option value="current">Current</option>
                  <option value="today">Today</option>
                </select>
              </div>

              {/* Signup Only Filter */}
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="signupOnly"
                  checked={filters.signupOnly}
                  onChange={(e) => setFilters(prev => ({ 
                    ...prev, 
                    signupOnly: e.target.checked 
                  }))}
                  className="h-4 w-4 text-fwo-blue focus:ring-fwo-blue border-gray-300 rounded"
                />
                <label htmlFor="signupOnly" className="ml-2 text-sm text-gray-700">
                  Signup required only
                </label>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
          <button
            onClick={loadEvents}
            className="mt-2 text-sm text-red-600 hover:text-red-800 underline"
          >
            Try again
          </button>
        </div>
      )}

      {/* Events List */}
      <div className="space-y-4">
        {filteredEvents.length === 0 ? (
          <div className="text-center py-8">
            <Calendar className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">
              {filters.searchQuery ? 'No events match your search.' : 'No events found.'}
            </p>
          </div>
        ) : (
          filteredEvents.map((event) => {
            const { dateStr, timeStr } = formatDateTime(event.startTime)
            const { timeStr: endTimeStr } = formatDateTime(event.endTime)
            const signedUp = isSignedUp(event)
            const eventFull = isEventFull(event)
            const eventPast = isEventPast(event)
            const canSignup = event.signupRequired && !eventPast && !eventFull
            const isLoading = signupLoading === event.eventId

            return (
              <div
                key={event.eventId}
                className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    {/* Event Header */}
                    <div className="flex items-center space-x-3 mb-2">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${EVENT_TYPE_COLORS[event.eventType]}`}>
                        {EVENT_TYPE_LABELS[event.eventType]}
                      </span>
                      {eventPast && (
                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                          Past
                        </span>
                      )}
                    </div>

                    {/* Event Title */}
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      {event.title}
                    </h3>

                    {/* Event Description */}
                    <div className="text-gray-600 mb-4">
                      <SafeHtmlContent content={event.description} className="text-gray-600" />
                    </div>

                    {/* Event Details */}
                    <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
                      <div className="flex items-center space-x-1">
                        <Calendar className="h-4 w-4" />
                        <span>{dateStr}</span>
                      </div>
                      
                      <div className="flex items-center space-x-1">
                        <Clock className="h-4 w-4" />
                        <span>{timeStr} - {endTimeStr}</span>
                      </div>

                      {event.location && (
                        <div className="flex items-center space-x-1">
                          <MapPin className="h-4 w-4" />
                          <span>{event.location}</span>
                        </div>
                      )}

                      {event.signupRequired && (
                        <div className="flex items-center space-x-1">
                          <Users className="h-4 w-4" />
                          <span>
                            {event.participants.length}
                            {event.maxParticipants && ` / ${event.maxParticipants}`}
                            {event.maxParticipants ? ' participants' : ' signed up'}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Signup Button */}
                  {canSignup && (
                    <div className="ml-4">
                      {signedUp ? (
                        <button
                          onClick={() => handleWithdraw(event.eventId)}
                          disabled={isLoading}
                          className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          {isLoading ? 'Withdrawing...' : 'Withdraw'}
                        </button>
                      ) : (
                        <button
                          onClick={() => handleSignup(event.eventId)}
                          disabled={isLoading || !!eventFull}
                          className="px-4 py-2 bg-fwo-blue text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          {isLoading ? 'Signing up...' : eventFull ? 'Full' : 'Sign Up'}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )
          })
        )}
      </div>
    </div>
  )
}